import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

const UniversalReferenceModal = ({ 
  isOpen, 
  onClose, 
  referenceType, 
  config, 
  itemData, 
  onSubmit,
  loading = false,
  viewMode = false,
  isEditPossible = true,
  error = null,
  onClearError = () => {}
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // Инициализация формы
  useEffect(() => {
    if (config && config.fields) {
      const initialData = {};
      Object.keys(config.fields).forEach(field => {
        initialData[field] = itemData?.[field] || itemData?.additionalFields?.[field] || '';
      });
      setFormData(initialData);
      setErrors({});
    }
  }, [config, itemData, isOpen]);

  // Очистка ошибок при открытии/закрытии модального окна
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      onClearError();
    }
  }, [isOpen, onClearError]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Очищаем ошибку при изменении
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Очищаем общую ошибку при изменении любого поля
    if (error) {
      onClearError();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация
    const newErrors = {};
    if (config && config.fields) {
      Object.entries(config.fields).forEach(([field, fieldConfig]) => {
        if (fieldConfig.required && !formData[field]) {
          newErrors[field] = `${fieldConfig.label} обов'язкове поле`;
        }
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(formData);
      // onClose() вызывается в родительском компоненте после успешного сохранения
    } catch (error) {
      // Ошибка обрабатывается в родительском компоненте
    }
  };

  const renderField = (field, fieldConfig) => {
    const value = formData[field] || '';
    const fieldError = errors[field];

    const commonProps = {
      value: value,
      onChange: (e) => handleChange(field, e.target.value),
      isInvalid: !!fieldError,
      placeholder: `Введіть ${fieldConfig.label.toLowerCase()}`,
      readOnly: viewMode,
      disabled: viewMode || loading || !isEditPossible
    };

    switch (fieldConfig.type) {
      case 'select':
        return (
          <Form.Select {...commonProps}>
            <option value="">Оберіть...</option>
            {fieldConfig.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );
      
      case 'textarea':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            {...commonProps}
          />
        );
      
      case 'number':
        return (
          <Form.Control
            type="number"
            {...commonProps}
          />
        );
      
      default:
        return (
          <Form.Control
            type="text"
            {...commonProps}
          />
        );
    }
  };

  if (!config) return null;

  const getModalTitle = () => {
    if (viewMode) {
      return `Перегляд елемента - ${config.name}`;
    }
    return itemData 
      ? `Редагувати елемент - ${config.name}` 
      : `Додати новий елемент - ${config.name}`;
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {getModalTitle()}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Отображение общей ошибки - ДОБАВЛЕНО УСЛОВИЕ ДЛЯ ОТОБРАЖЕНИЯ */}
          {error && (
            <Alert variant="danger" dismissible onClose={onClearError}>
              {error}
            </Alert>
          )}

          {config.fields && Object.entries(config.fields).map(([field, fieldConfig]) => (
            <Form.Group key={field} className="mb-3">
              <Form.Label>
                {fieldConfig.label}
                {fieldConfig.required && !viewMode && <span className="text-danger"> *</span>}
              </Form.Label>
              {renderField(field, fieldConfig)}
              <Form.Control.Feedback type="invalid">
                {errors[field]}
              </Form.Control.Feedback>
              {fieldConfig.description && (
                <Form.Text className="text-muted">
                  {fieldConfig.description}
                </Form.Text>
              )}
            </Form.Group>
          ))}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {viewMode ? 'Закрити' : 'Скасувати'}
          </Button>
          {!viewMode && isEditPossible && (
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Збереження...' : (itemData ? 'Зберегти зміни' : 'Додати')}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UniversalReferenceModal;